import { Button } from '@/common/ui/Form/Button'
import { TokenAmount } from '@/common/ui/TokenAmount'
import { cn } from '@/common/lib/cn/cn'
import { formatSubscriptionChargeInterval } from '../lib/formatSubscriptionPeriod'
import { useSubscribeForm } from '../model/useSubscribeForm'

type SubscribeFormProps = {
    onSuccess?: () => void
    className?: string
}

export const SubscribeForm = ({ onSuccess, className }: SubscribeFormProps) => {
    const {
        subscriptions,
        isLoading,
        loadError,
        selectedSubscriptionId,
        isPaying,
        isWidgetReady,
        resolvedError,
        handleSubscriptionSelect,
        handleSubmit,
    } = useSubscribeForm({ onSuccess })

    if (isLoading) {
        return (
            <div className={cn('grid grid-cols-1 gap-2 md:grid-cols-3 w-full', className)}>
                {[1, 2, 3].map(item => (
                    <div
                        key={item}
                        className='animate-pulse rounded-[12px] bg-[rgba(255,255,255,0.05)] min-h-[180px]'
                        aria-hidden='true'
                    />
                ))}
            </div>
        )
    }

    if (loadError) {
        return (
            <p className={cn('text-sm text-[#ff8a8a]', className)} role='alert'>
                Не удалось загрузить тарифы подписки.
            </p>
        )
    }

    if (subscriptions.length === 0) {
        return <p className={cn('text-sm text-[#aba7af]', className)}>Тарифы подписки временно недоступны.</p>
    }

    return (
        <form className={cn('flex flex-col gap-4 w-full', className)} onSubmit={handleSubmit}>
            <div className='grid grid-cols-1 gap-2 md:grid-cols-3 w-full'>
                {subscriptions.map(plan => {
                    const isActive = selectedSubscriptionId === plan.id
                    const chargeIntervalLabel = formatSubscriptionChargeInterval(plan.period, plan.num_periods)

                    return (
                        <label
                            key={plan.id}
                            className={cn(
                                'flex flex-col items-center justify-center gap-4 py-[1.5rem] px-8 rounded-[12px] bg-[rgba(255,255,255,0.02)] transition-colors cursor-pointer',
                                { 'ring-1 ring-[#be96ff] bg-[rgba(255,255,255,0.05)]': isActive }
                            )}
                            aria-label={`${plan.name} на ${plan.tokens} токенов`}
                            tabIndex={0}
                            onKeyDown={event => {
                                if (event.key !== 'Enter' && event.key !== ' ') return
                                event.preventDefault()
                                handleSubscriptionSelect(plan.id)
                            }}
                        >
                            <input
                                type='radio'
                                name='subscriptionId'
                                value={plan.id}
                                checked={isActive}
                                onChange={() => handleSubscriptionSelect(plan.id)}
                                className='sr-only'
                                aria-checked={isActive}
                            />
                            <TokenAmount value={plan.tokens} />
                            <div className='flex flex-col items-center gap-1'>
                                {plan.old_price != null && (
                                    <span className='text-sm font-medium text-[#aba7af] leading-[1.3] line-through'>
                                        {plan.old_price.toLocaleString('ru-RU')} ₽
                                    </span>
                                )}
                                <div className='text-2xl font-semibold text-white leading-[1.4]'>
                                    {plan.price.toLocaleString('ru-RU')} ₽
                                </div>
                            </div>
                            <div className='text-xs text-[var(--pixorion-approval)] leading-[1.3] font-medium text-center'>
                                {plan.name}
                            </div>
                            <div className='text-xs text-[#aba7af] leading-[1.3] text-center'>
                                {chargeIntervalLabel}
                            </div>
                        </label>
                    )
                })}
            </div>
            {resolvedError && (
                <p className='text-sm text-[#ff8a8a]' role='alert'>
                    {resolvedError}
                </p>
            )}
            <div className='flex items-center justify-end gap-2 w-full'>
                <Button
                    size='lg'
                    emphasis='accent'
                    className={cn('px-6', {
                        'text-white': selectedSubscriptionId && isWidgetReady && !isPaying,
                        'text-[#aba7af]': !selectedSubscriptionId || !isWidgetReady || isPaying,
                    })}
                    disabled={!selectedSubscriptionId || !isWidgetReady || isPaying}
                    loading={isPaying}
                    type='submit'
                >
                    Оплатить
                </Button>
            </div>
        </form>
    )
}
